import { listQuestionAnswers, listQuestions, listGuidances, listRoadMapItems, listQuestionGroups } from '../graphql/queries';
import React, { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';

import { createQuestionAnswers, updateQuestionAnswers, deleteQuestionAnswers } from '../graphql/mutations';

import { makeStyles } from '@material-ui/core/styles';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';

import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
  },
  questionTitle: {
    fontWeight: 'bold',
    fontSize: 16,
  },
  textField: {
    margin: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '50%',
    },
  },
  uploadButton: {
    margin: theme.spacing(1),
  },
  submitButton: {
    margin: theme.spacing(3, 0),
  },
  guidanceContentItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  tableContainer: {
    boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.1)',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '15px',
    backgroundColor: '#f9f9f9',
  },
  tableHeader: {
    backgroundColor: '#01b5e2',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#e5f7fb',
    },
  },
  tableCell: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.9rem',
  },
}));

const EditQuestionForm = () => {
  const classes = useStyles();
  const location = useLocation();
  const { id, question, helpText, groupID } = location.state;

  const [answerList, setAnswerList] = useState([]);
  const [newAnswer, setNewAnswer] = useState('');
  const [newHelpText, setNewHelpText] = useState('');
  const [questionList, setQuestionList] = useState([]);
  const [guidanceList, setGuidanceList] = useState([]);
  const [roadmapList, setRoadmapList] = useState([]);
  const [questionGroupList, setQuestionGroupList] = useState([]);
  const [questionAnswersGuidanceContentId, setQuestionAnswersGuidanceContentId] = useState('');
  const [questionAnswersRoadMapItemsId, setQuestionAnswersRoadMapItemsId] = useState('');
  const [questionAnswersQuestionGroupId, setQuestionAnswersQuestionGroupId] = useState('');
  const [nextAnswerQuestionId, setNextAnswerQuestionId] = useState('');

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);

  const params = useParams();

  useEffect(() => {
    fetchAnswerList();
    fetchQuestionList();
    fetchGuidanceList();
    fetchRoadMapList();
    fetchQuestionGroupList();
  }, []);

  const fetchQuestionList = async () => {
    try {
      let allQuestions = [];
      let nextToken = null;
  
      do {
        const response = await API.graphql(
          graphqlOperation(listQuestions, {
            filter: {
              questionsQuestionGroupId: { eq: groupID },
            },
            nextToken,
          })
        );
        const qList = response.data?.listQuestions?.items || [];
        allQuestions = [...allQuestions, ...qList];
        nextToken = response.data?.listQuestions?.nextToken;
      } while (nextToken);
  
      const sortedList = allQuestions.sort((a, b) => a.questionNumber - b.questionNumber);
      setQuestionList(sortedList);
    } catch (error) {
      console.log(error);
    }
  };
  
  const fetchAnswerList = async () => {
    try {
      let allAnswers = [];
      let nextToken = null;
  
      do {
        const response = await API.graphql(
          graphqlOperation(listQuestionAnswers, {
            filter: {
              questionAnswersQuestionsId: { eq: id },
            },
            nextToken,
          })
        );
        const aList = response.data?.listQuestionAnswers?.items || [];
        allAnswers = [...allAnswers, ...aList];
        nextToken = response.data?.listQuestionAnswers?.nextToken;
      } while (nextToken);
  
      setAnswerList(allAnswers);
    } catch (error) {
      console.log(error);
    }
  };
  
  const fetchGuidanceList = async () => {
    try {
      let allGuidances = [];
      let nextToken = null;
  
      do {
        const response = await API.graphql(
          graphqlOperation(listGuidances, { nextToken })
        );
        const gList = response.data?.listGuidances?.items || [];
        allGuidances = [...allGuidances, ...gList];
        nextToken = response.data?.listGuidances?.nextToken;
      } while (nextToken);
  
      const sortedList = allGuidances.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateA - dateB;
      });
  
      setGuidanceList(sortedList);
    } catch (error) {
      console.log(error);
    }
  };
  
  const fetchRoadMapList = async () => {
    try {
      let allRoadMapItems = [];
      let nextToken = null;
  
      do {
        const response = await API.graphql(
          graphqlOperation(listRoadMapItems, { nextToken })
        );
        const rList = response.data?.listRoadMapItems?.items || [];
        allRoadMapItems = [...allRoadMapItems, ...rList];
        nextToken = response.data?.listRoadMapItems?.nextToken;
      } while (nextToken);
  
      const sortedList = allRoadMapItems.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateA - dateB;
      });
  
      setRoadmapList(sortedList);
    } catch (error) {
      console.log(error);
    }
  };
  
  const fetchQuestionGroupList = async () => {
    try {
      let allQuestionGroups = [];
      let nextToken = null;
  
      do {
        const response = await API.graphql(
          graphqlOperation(listQuestionGroups, { nextToken })
        );
        const qgList = response.data?.listQuestionGroups?.items || [];
        allQuestionGroups = [...allQuestionGroups, ...qgList];
        nextToken = response.data?.listQuestionGroups?.nextToken;
      } while (nextToken);
  
      setQuestionGroupList(allQuestionGroups);
    } catch (error) {
      console.log(error);
    }
  };

  
  const handleAddAnswer = async (event) => {
    event.preventDefault();
    try {
      await API.graphql(
        graphqlOperation(createQuestionAnswers, {
          input: {
            answer: newAnswer,
            helpText: newHelpText,
            questionAnswersQuestionsId: id,
            nextQuestionID: nextAnswerQuestionId,
            questionAnswersGuidanceContentId: questionAnswersGuidanceContentId,
            questionAnswersRoadMapItemsId: questionAnswersRoadMapItemsId,
            questionAnswersQuestionGroupId: questionAnswersQuestionGroupId,
          },
        })
      );
      setNewAnswer('');
      setNewHelpText('');
      setNextAnswerQuestionId('');
      setQuestionAnswersGuidanceContentId('');
      setQuestionAnswersRoadMapItemsId('');
      setQuestionAnswersQuestionGroupId('');
      fetchAnswerList();
    } catch (error) {
      console.log(error);
    }
  };

  const handleEdit = (answerId) => {
    const updatedAnswerList = answerList.map((answer) =>
      answer.id === answerId ? { ...answer, isEditing: true } : answer
    );
    setAnswerList(updatedAnswerList);
  };

  const handleSaveEdit = async (answerId) => {
    const editedAnswer = answerList.find((answer) => answer.id === answerId);
    try {
      await API.graphql(
        graphqlOperation(updateQuestionAnswers, {
          input: {
            id: editedAnswer.id,
            answer: editedAnswer.answer,
            helpText: editedAnswer.helpText,
            questionAnswersGuidanceContentId: editedAnswer.questionAnswersGuidanceContentId,
            questionAnswersRoadMapItemsId: editedAnswer.questionAnswersRoadMapItemsId,
            nextQuestionID: editedAnswer.nextQuestionID,
            questionAnswersQuestionGroupId: editedAnswer.questionAnswersQuestionGroupId,
          },
        })
      );
      const updatedAnswerList = answerList.map((answer) =>
        answer.id === answerId ? { ...answer, isEditing: false } : answer
      );
      setAnswerList(updatedAnswerList);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancelEdit = (answerId) => {
    const updatedAnswerList = answerList.map((answer) =>
      answer.id === answerId ? { ...answer, isEditing: false } : answer
    );
    setAnswerList(updatedAnswerList);
  };

  const handleDeleteConfirmationOpen = (itemId) => {
    setSelectedItemId(itemId);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setSelectedItemId(null);
    setDeleteConfirmationOpen(false);
  };

  const handleDeleteItem = async () => {
    try {
      if (selectedItemId) {
        await API.graphql(
          graphqlOperation(deleteQuestionAnswers, { input: { id: selectedItemId } })
        );
        fetchAnswerList();
      }
    } catch (error) {
      console.log(error);
    }
    handleDeleteConfirmationClose();
  };

  return (
    <>
      <Box className={classes.root} component="form">
        <TextField
          className={classes.textField}
          label="Question"
          value={question}
        />
      </Box>

      <Box className={classes.root} component="form" onSubmit={handleAddAnswer}>
        <Typography variant="h6" component="h2" gutterBottom>
          Add New Answer
        </Typography>
        <TextField
          className={classes.textField}
          required
          label="Answer"
          value={newAnswer}
          onChange={(event) => setNewAnswer(event.target.value)}
        />
        <TextField
          className={classes.textField}
          required
          label="Help Text"
          multiline
          minRows={4}
          value={newHelpText}
          onChange={(event) => setNewHelpText(event.target.value)}
        />
        <FormControl className={classes.textField}>
          <InputLabel id="guidance-label">Guidance</InputLabel>
          <Select
            labelId="guidance-label"
            value={questionAnswersGuidanceContentId}
            onChange={(event) => setQuestionAnswersGuidanceContentId(event.target.value)}
          >
            <MenuItem value={null}>None</MenuItem>
            {guidanceList.map((guidance) => (
              <MenuItem key={guidance.id} value={guidance.id}>
                {guidance.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className={classes.textField}>
          <InputLabel id="roadmap-label">Guidance Action</InputLabel>
          <Select
            labelId="roadmap-label"
            value={questionAnswersRoadMapItemsId}
            onChange={(event) => setQuestionAnswersRoadMapItemsId(event.target.value)}
          >
            <MenuItem value={null}>None</MenuItem>
            {roadmapList.map((roadmap) => (
              <MenuItem key={roadmap.id} value={roadmap.id}>
                {roadmap.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className={classes.textField}>
          <InputLabel id="next-answer-question-label">Next Question</InputLabel>
          <Select
            labelId="next-answer-question-label"
            value={nextAnswerQuestionId}
            onChange={(event) => setNextAnswerQuestionId(event.target.value)}
          >
            <MenuItem value={null}>None</MenuItem>
            {questionList.map((question) => (
              <MenuItem key={question.id} value={question.id}>
                {`${question.questionNumber} - ${question.question}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className={classes.textField}>
          <InputLabel id="question-group-label">Question Group</InputLabel>
          <Select
            labelId="question-group-label"
            value={questionAnswersQuestionGroupId}
            onChange={(event) => setQuestionAnswersQuestionGroupId(event.target.value)}
          >
            <MenuItem value={null}>None</MenuItem>
            {questionGroupList.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button className={classes.submitButton} variant="contained" color="primary" type="submit">
          Add Answer
        </Button>
      </Box>

      <Table className={classes.tableContainer}>
        <TableHead>
          <TableRow className={classes.tableHeader}>
            <TableCell>ID</TableCell>
            <TableCell>Answer</TableCell>
            <TableCell>Help Text</TableCell>
            <TableCell>Guidance Article</TableCell>
            <TableCell>Guidance Action</TableCell>
            <TableCell>Next Question</TableCell>
            <TableCell>Question Group</TableCell>
            <TableCell>Actions</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {answerList.map((answerItem) => (
            <TableRow key={answerItem.id} className={classes.tableRow}>
              <TableCell>{answerItem.id}</TableCell>
              <TableCell>
                {answerItem.isEditing ? (
                  <TextField
                    value={answerItem.answer}
                    onChange={(e) =>
                      setAnswerList(
                        answerList.map((answer) =>
                          answer.id === answerItem.id ? { ...answer, answer: e.target.value } : answer
                        )
                      )
                    }
                  />
                ) : (
                  answerItem.answer
                )}
              </TableCell>
              <TableCell>
                {answerItem.isEditing ? (
                  <TextField
                    value={answerItem.helpText}
                    onChange={(e) =>
                      setAnswerList(
                        answerList.map((answer) =>
                          answer.id === answerItem.id ? { ...answer, helpText: e.target.value } : answer
                        )
                      )
                    }
                  />
                ) : (
                  answerItem.helpText
                )}
              </TableCell>
              <TableCell>
                {answerItem.isEditing ? (
                  <FormControl className={classes.textField}>
                    <Select
                      labelId={`guidance-label-${answerItem.id}`}
                      value={answerItem?.questionAnswersGuidanceContentId}
                      onChange={(event) => {
                        setAnswerList(
                          answerList.map((answer) =>
                            answer.id === answerItem.id ? { ...answer, questionAnswersGuidanceContentId: event.target.value } : answer
                          )
                        );
                      }}
                    >
                      <MenuItem value={null}>None</MenuItem>
                      {guidanceList.map((guidance) => (
                        <MenuItem key={guidance.id} value={guidance.id}>
                          {guidance.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  answerItem?.GuidanceContent?.title
                )}
              </TableCell>
              <TableCell>
                {answerItem.isEditing ? (
                  <FormControl className={classes.textField}>
                    <Select
                      labelId={`roadmap-label-${answerItem.id}`}
                      value={answerItem.questionAnswersRoadMapItemsId}
                      onChange={(event) => {
                        setAnswerList(
                          answerList.map((answer) =>
                            answer.id === answerItem.id ? { ...answer, questionAnswersRoadMapItemsId: event.target.value } : answer
                          )
                        );
                      }}
                    >
                      <MenuItem value={null}>None</MenuItem>
                      {roadmapList.map((roadmap) => (
                        <MenuItem key={roadmap.id} value={roadmap.id}>
                          {roadmap.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  answerItem?.RoadMapItems?.text
                )}
              </TableCell>
              <TableCell>
                {answerItem.isEditing ? (
                  <FormControl className={classes.textField}>
                    <Select
                      labelId={`next-answer-question-label-${answerItem.id}`}
                      value={answerItem.nextQuestionID}
                      onChange={(event) => {
                        setAnswerList(
                          answerList.map((answer) =>
                            answer.id === answerItem.id ? { ...answer, nextQuestionID: event.target.value } : answer
                          )
                        );
                      }}
                    >
                      <MenuItem value={null}>None</MenuItem>
                      {questionList.map((question) => (
                        <MenuItem key={question.id} value={question.id}>
                          {question.question}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  questionList.find((q) => q.id === answerItem.nextQuestionID)?.question || answerItem.nextQuestionID
                )}
              </TableCell>
              <TableCell>
                {answerItem.isEditing ? (
                  <FormControl className={classes.textField}>
                    <Select
                      labelId={`question-group-label-${answerItem.id}`}
                      value={answerItem.questionAnswersQuestionGroupId}
                      onChange={(event) => {
                        setAnswerList(
                          answerList.map((answer) =>
                            answer.id === answerItem.id ? { ...answer, questionAnswersQuestionGroupId: event.target.value } : answer
                          )
                        );
                      }}
                    >
                      <MenuItem value={null}>None</MenuItem>
                      {questionGroupList.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  questionGroupList.find((g) => g.id === answerItem.questionAnswersQuestionGroupId)?.name
                )}
              </TableCell>
              <TableCell>
                {answerItem.isEditing ? (
                  <>
                    <Button
                      color="primary"
                      onClick={() => handleSaveEdit(answerItem.id)}
                    >
                      Save
                    </Button>
                    <Button
                      color="secondary"
                      onClick={() => handleCancelEdit(answerItem.id)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <IconButton color="primary" onClick={() => handleEdit(answerItem.id)}>
                    <EditIcon />
                  </IconButton>
                )}
              </TableCell>
              <TableCell>
                <IconButton
                  color="secondary"
                  onClick={() => handleDeleteConfirmationOpen(answerItem.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={deleteConfirmationOpen} onClose={handleDeleteConfirmationClose}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this answer?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={handleDeleteConfirmationClose}>
            Cancel
          </Button>
          <Button color="secondary" onClick={handleDeleteItem}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditQuestionForm;
